import { useCallback, useEffect, useState } from "react";
import { api, fmtPrice } from "@/lib/api";
import { toast } from "sonner";

export default function AdminApprovals() {
  const [data, setData] = useState({ projects: [], communities: [] });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setBusy(true);
    api.get("/admin/drafts").then((r) => setData(r.data)).finally(() => setBusy(false));
  }, []);
  useEffect(load, [load]);

  const act = async (type, id, action) => {
    await api.post(`/admin/approve/${type}/${id}?action=${action}`);
    toast.success(`${type} ${action}d`);
    load();
  };

  return (
    <div className="p-10" data-testid="admin-approvals">
      <div className="mb-3 text-xs tracking-[0.24em] uppercase text-gray-500">Governance</div>
      <h1 className="font-serif text-4xl mb-2">Approvals</h1>
      <p className="text-sm text-gray-600 max-w-3xl mb-8">
        Imported or newly created records land here as drafts. Review, edit and publish only what meets Point Penta&apos;s
        data quality standards.
      </p>

      {busy && <p className="text-sm text-gray-500">Loading…</p>}

      <section className="mb-10">
        <h2 className="font-serif text-2xl mb-4">Projects <span className="text-sm text-gray-500">({data.projects.length})</span></h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.projects.map((p) => <ApprovalCard key={p.id} item={p} type="project" onAct={act} />)}
          {data.projects.length === 0 && <p className="text-sm text-gray-500 col-span-2">No project drafts. Everything is up to date.</p>}
        </div>
      </section>

      <section>
        <h2 className="font-serif text-2xl mb-4">Communities <span className="text-sm text-gray-500">({data.communities.length})</span></h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.communities.map((c) => <ApprovalCard key={c.id} item={c} type="community" onAct={act} />)}
          {data.communities.length === 0 && <p className="text-sm text-gray-500 col-span-2">No community drafts.</p>}
        </div>
      </section>
    </div>
  );
}

function ApprovalCard({ item, type, onAct }) {
  return (
    <div className="border border-[#E5E7EB] bg-white p-5" data-testid={`approval-card-${item.slug || item.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-gray-500">{type}</div>
          <h3 className="font-serif text-2xl mt-1">{item.name}</h3>
          <div className="text-sm text-gray-600 mt-1">
            {item.community || item.city || "—"} · {item.import_source || "manual"}
          </div>
        </div>
        <span className={`text-xs px-2 py-1 border ${
          item.publish_status === "review" ? "bg-amber-50 border-amber-200 text-amber-800" :
          "bg-gray-50 border-gray-200 text-gray-700"
        }`}>{item.publish_status || "draft"}</span>
      </div>

      {type === "project" && (
        <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-3">
          <div>Status: <b>{item.status || "—"}</b></div>
          <div>Handover: <b>{item.handover || "—"}</b></div>
          <div>From: <b>{item.starting_price ? fmtPrice(item.starting_price, item.currency) : "—"}</b></div>
          <div>Pin: <b>{item.latitude ? `${item.latitude.toFixed(3)}, ${item.longitude.toFixed(3)}` : "—"}</b></div>
        </div>
      )}

      {type === "community" && (
        <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-3">
          <div>Emirate: <b>{item.emirate || "—"}</b></div>
          <div>Yield: <b>{item.rental_yield ? `${item.rental_yield}%` : "—"}</b></div>
          <div>Invest: <b>{item.investment_score || "—"}</b></div>
          <div>Pin: <b>{item.latitude ? `${item.latitude.toFixed(3)}, ${item.longitude.toFixed(3)}` : "—"}</b></div>
        </div>
      )}

      <div className="flex gap-2 mt-4">
        <button
          data-testid={`approval-publish-${item.slug}`}
          onClick={() => onAct(type, item.id, "publish")}
          className="px-4 py-2 text-xs uppercase tracking-[0.2em] bg-[#001588] text-white hover:bg-black"
        >Publish</button>
        <button
          onClick={() => onAct(type, item.id, "review")}
          className="px-4 py-2 text-xs uppercase tracking-[0.2em] bg-amber-500 text-white hover:bg-amber-600"
        >Mark for review</button>
        <button
          onClick={() => onAct(type, item.id, "reject")}
          className="px-4 py-2 text-xs uppercase tracking-[0.2em] bg-white text-red-600 border border-red-600 hover:bg-red-50"
        >Send back to draft</button>
      </div>
    </div>
  );
}
